



































import Component from "vue-class-component";

import { RingLoader } from "vue-spinners-css";

import { IdnAndClients, Client } from "@/models";
import _auth from "@/utils/auth";
import _gateway from "@/utils/gateway";
import { BaseRootPageComponent } from "./BaseRootPageComponent";
import { EventBus, EventNames } from "@/utils/eventBus";
import { updateGTMGlobalVariables } from "@/utils/gtm";

@Component({
  components: {
    RingLoader
  }
})
export default class SelectHospital extends BaseRootPageComponent {
  // Use a class property for dependencies so unit tests can override them
  auth = _auth;
  gateway = _gateway;

  isDestroyed = false;

  loading = true;
  idns: IdnAndClients[] = [];
  // Need to initialize these fields, otherwise vue won't render them
  selectedIdn?: IdnAndClients = {} as IdnAndClients;
  selectedClient?: Client = {} as Client;

  get hasMultipleIdns() {
    return this.idns.length > 1;
  }

  get clients() {
    return this.selectedIdn?.clients || [];
  }

  get allClientIds() {
    return this.auth.user?.clientIds || [];
  }

  async created() {
    this.setTitle("Select Hospital");

    // updateGTMGlobalVariables(this);

    try {
      this.idns = await this.gateway.getIdnsAndClients();
      this.selectInitialIdnAndClient();

      // If the user only has access to a single client, automatically select use client and move on
      if (this.allClientIds.length === 1) {
        this.submit();
        return;
      }

      // Subscribe after loading the hospitals, so that we don't refresh while loading the hospitals
      this.subscribeToEvents();
    } finally {
      this.loading = false;
    }
  }

  subscribeToEvents() {
    // If the component has already been destroyed (e.g. the user navigated away while the initial data was being
    // loaded), don't register for the data refresh event otherwise, it will never be removed and will keep running
    // until the user reloads the webpage.
    if (this.isDestroyed) return;

    EventBus.$on(EventNames.DataRefresh, this.refreshData);
  }

  beforeDestroy() {
    EventBus.$off(EventNames.DataRefresh, this.refreshData);
    this.isDestroyed = true;
  }

  async refreshData() {
    // If somehow the component was already destroyed, don't bother refreshing
    if (this.isDestroyed) return;

    this.idns = await this.gateway.getIdnsAndClients();

    // If the selected IDN is removed (unlikely), selectedIdn will be cleared asking the user to select an IDN
    this.selectedIdn = this.idns.find(i => i.id === this.selectedIdn?.id);
    if (this.selectedIdn) {
      this.selectedClient = this.selectedIdn.clients.find(c => c.id === this.selectedClient?.id);
    } else {
      // If the IDN was cleared, ensure the client is also cleared
      this.selectedClient = undefined;
    }
  }

  /**
   * After the IDN and Clients are loaded, this is used to automatically select the appropriate IDn and client, using
   * the following rules:
   *  1) If the user only has access to a single client, that client is selected
   *  2) If the user already has a client saved to the auth service, that client is selected
   *  3) Otherwise, the first client of the first IDN is selected
   */
  private selectInitialIdnAndClient() {
    // If the user only has access to a single client or already has a client selected, use the client and idn ids
    const clientId = this.allClientIds.length === 1 ? this.allClientIds[0] : this.auth.clientId;
    if (clientId) {
      // Search for the IDN containing the current client
      const idn = this.idns.find(i => i.clients.some(c => c.id === clientId));

      // If a match was found, assign it and stop processing
      if (idn) {
        this.selectedIdn = idn;
        this.selectedClient = idn.clients.find(c => c.id === clientId);
        return;
      }
    }

    // If the user doesn't already have a client selected (or the selection wasn't found), select the first IDN and client
    this.selectedIdn = this.idns[0];
    this.selectedClient = this.selectedIdn?.clients[0];
  }

  idnChanged() {
    // When the IDN is changed, change the selected client to the IDN's first client
    this.selectedClient = this.selectedIdn?.clients[0];
  }

  async submit() {
    if (!this.selectedIdn || !this.selectedClient) return;

    await this.gateway.changeClient(this.selectedClient.id,this.auth.pushInfo);
    this.auth.setIdnId(this.selectedIdn.id);
    this.auth.setClientId(this.selectedClient.id);
    this.$router.push("/");
  }
}
